import { BehaviorSubject, fromEvent, Subject, Subscription } from 'rxjs';
import { ElementRef, Injectable, OnDestroy, signal } from '@angular/core';

@Injectable({
    providedIn: 'root',
})
export class LayoutService implements OnDestroy {
    private _isCollapsedSubject = new BehaviorSubject<boolean>(false);
    private _isHidden = false;
    private _onScroll = new Subject<Event>();
    // Signal (not BehaviorSubject): the header-hidden host binding reads this
    // during change detection; a signal write schedules a proper refresh
    // instead of silently changing a checked value (NG0100).
    private _headerHidden = signal(false);

    onScroll$ = this._onScroll.asObservable();

    private _scrollContainer!: HTMLElement;
    private _scrollSubscription?: Subscription;
    private _menuVisible = signal(false);
    private _pullDownRefreshAction?: (event: Subject<void>) => void;
    private _isPullDownRefreshEnabled = false;
    // Signal for the same reason as _headerHidden: the app layout template
    // binds this, and ngAfterViewInit assigns it during the same pass.
    private _scrollToRefreshContainer = signal<HTMLElement | undefined>(
        undefined
    );

    get headerHidden() {
        return this._headerHidden();
    }

    set scrollContainer(container: HTMLElement) {
        this._scrollContainer = container;
        this.listenForScrollEvents();
    }

    get scrollContainer() {
        return this._scrollContainer;
    }

    set scrollToRefreshContainer(container: HTMLElement) {
        this._scrollToRefreshContainer.set(container);
    }

    get scrollToRefreshContainer() {
        return this._scrollToRefreshContainer() as HTMLElement;
    }

    private _activeScrollAtTop = true;

    get isPullDownRefreshEnabled() {
        // Only allow the pull when the content the user is touching is actually
        // scrolled to its top (set at touchstart by the app layout), so a
        // normal scroll flick inside an inner container can't trigger a refresh.
        return this._isPullDownRefreshEnabled && this._activeScrollAtTop;
    }

    /** App layout reports whether the touched scroll container is at its top. */
    setActiveScrollAtTop(atTop: boolean) {
        this._activeScrollAtTop = atTop;
    }

    get isHidden() {
        if (localStorage.getItem('isMenuHidden') !== null) {
            this._isHidden = localStorage.getItem('isMenuHidden') === 'true';
        }
        return this._isHidden;
    }

    set isHidden(value) {
        localStorage.setItem('isMenuHidden', value.toString());
        this._isHidden = value;
    }

    get isCollapsed() {
        return this._isCollapsedSubject.value;
    }

    set isCollapsed(value) {
        this._isCollapsedSubject.next(value);
    }

    get pullDownRefreshAction() {
        return this._pullDownRefreshAction ?? (() => window.location.reload());
    }

    isCollapsed$ = this._isCollapsedSubject.asObservable();

    get menuVisible() {
        return this._menuVisible();
    }

    ngOnDestroy(): void {
        this._scrollSubscription?.unsubscribe();
    }

    private listenForScrollEvents() {
        this._scrollSubscription?.unsubscribe();
        this._scrollSubscription = fromEvent(
            this.scrollContainer,
            'scroll'
        ).subscribe(event => this._onScroll.next(event));
    }

    openMenu() {
        this._menuVisible.set(true);
    }

    closeMenu() {
        this._menuVisible.set(false);
    }

    hideHeader() {
        this._headerHidden.set(true);
    }

    showHeader() {
        this._headerHidden.set(false);
    }

    setPullDownRefreshEnabled(value: boolean) {
        this._isPullDownRefreshEnabled = value;
    }

    setPullDownRefreshAction(
        action: (event: Subject<void>) => void,
        targetElement?: ElementRef<HTMLElement>
    ) {
        if (targetElement) {
            this.scrollToRefreshContainer = targetElement.nativeElement;
        } else {
            this.scrollToRefreshContainer = this.scrollContainer;
        }
        this._pullDownRefreshAction = action;
    }

    clearPullDownRefreshAction() {
        this._pullDownRefreshAction = undefined;
        this.scrollToRefreshContainer = this.scrollContainer;
    }
}
